import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { apiRequest } from '../lib/api';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiShield, FiAlertCircle, FiLock, FiExternalLink } = FiIcons;

const StripePayment = ({ amount, currency = 'USD', onClose, paymentDetails }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const startCheckout = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Config
        const configDoc = await getDoc(doc(db, 'paymentConfig', 'stripe'));
        if (!configDoc.exists() || !configDoc.data().publicKey) {
          throw new Error("Stripe Public Key is missing in database.");
        }

        // 2. Create Session via Backend
        const response = await apiRequest('/create-checkout-session', {
          body: JSON.stringify({
            amount,
            currency,
            studentId: paymentDetails.studentId,
            studentName: paymentDetails.studentName,
            sessionKeys: paymentDetails.sessionKeys,
            paymentType: paymentDetails.paymentType
          })
        });

        if (response.error || !response.url) {
          throw new Error(response.message || "Failed to create checkout session.");
        }

        // 3. Direct Redirect to Stripe URL
        window.location.href = response.url;
      } catch (err) {
        console.error("Checkout Request Failed:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    startCheckout();
  }, [amount, currency, paymentDetails]);

  return (
    <div className="fixed inset-0 bg-gray-900/95 flex items-center justify-center p-4 z-[100] backdrop-blur-xl text-center">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white rounded-[3rem] shadow-2xl max-w-md w-full overflow-hidden border border-white/20 p-12"
      >
        {error ? (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-rose-50 rounded-[2rem] flex items-center justify-center mx-auto text-rose-500">
              <SafeIcon icon={FiAlertCircle} className="text-4xl" />
            </div>
            <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">Payment Error</h2>
            <p className="text-rose-600 text-sm font-bold leading-relaxed px-4">{error}</p>
            <button onClick={onClose} className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-black transition-all">Go Back</button>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center text-indigo-600">
                <SafeIcon icon={FiShield} className="text-3xl" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Redirecting...</h2>
              <p className="text-gray-400 text-[10px] font-black uppercase mt-2 tracking-widest">Securely connecting to Stripe Checkout</p>
            </div>
            <div className="flex items-center justify-center gap-2 py-3 px-6 bg-gray-50 rounded-2xl inline-flex text-indigo-600">
              <SafeIcon icon={FiLock} className="text-sm" />
              <span className="text-[10px] font-black uppercase tracking-widest">Encrypted Connection</span>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default StripePayment;